import React, { useEffect, useState } from "react";
import { Wallet, ShieldCheck, Receipt, Save } from "lucide-react";
import { toast } from "sonner";
import EarningsPanel from "@/components/EarningsPanel";
import StripeConnectPanel from "@/components/StripeConnectPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SectionBlock, Field, FieldGrid, SaveBar } from "./Channel";
import { useUnsaved } from "../DashboardShell";

/**
 * Payments — full financial pane. Balance + payouts + tax profile.
 * Tax fields are stored on the user document (only surfaced to KYC-verified
 * streamers who intend to withdraw; harmless if left blank).
 */
export default function Payments() {
  const { user } = useAuth();
  const [tax, setTax] = useState({
    legal_name: "", country: "", tax_id: "", w9_ack: false, address_line: "", city: "", region: "", postal: "",
  });
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const t = r.data?.tax_profile || {};
      const next = {
        legal_name: t.legal_name || "",
        country: t.country || "",
        tax_id: t.tax_id || "",
        w9_ack: !!t.w9_ack,
        address_line: t.address_line || "",
        city: t.city || "",
        region: t.region || "",
        postal: t.postal || "",
      };
      setTax(next);
      setInitial(next);
    }).catch(() => {});
  }, []);

  const dirty = initial && JSON.stringify(tax) !== JSON.stringify(initial);
  useUnsaved(dirty);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me/tax", tax);
      setInitial(tax);
      toast.success("Tax details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="section-payments">
      {/* Live earnings + withdraw */}
      <EarningsPanel />

      {/* Stripe Connect status + onboarding */}
      <SectionBlock icon={ShieldCheck} title="Payout Method" color="cyan">
        <StripeConnectPanel />
      </SectionBlock>

      {/* Tax info */}
      <SectionBlock icon={Receipt} title="Tax Information" color="orange">
        <p className="text-xs text-zinc-400 -mt-1">
          Required for US streamers earning above the IRS reporting threshold. Stored securely and only used for 1099
          preparation.
        </p>
        <FieldGrid>
          <Field label="Legal name">
            <input className="sc-input" value={tax.legal_name} onChange={(e) => setTax({ ...tax, legal_name: e.target.value })} data-testid="payments-tax-legal-name" />
          </Field>
          <Field label="Country">
            <input className="sc-input" value={tax.country} onChange={(e) => setTax({ ...tax, country: e.target.value })} placeholder="US, GB, CA…" data-testid="payments-tax-country" />
          </Field>
          <Field label="Tax ID / SSN (last 4)" hint="Never displayed after saving">
            <input className="sc-input" value={tax.tax_id} onChange={(e) => setTax({ ...tax, tax_id: e.target.value })} placeholder="●●●-●●-1234" data-testid="payments-tax-id" />
          </Field>
          <Field label="Address line" fullWidth>
            <input className="sc-input" value={tax.address_line} onChange={(e) => setTax({ ...tax, address_line: e.target.value })} data-testid="payments-tax-address" />
          </Field>
          <Field label="City">
            <input className="sc-input" value={tax.city} onChange={(e) => setTax({ ...tax, city: e.target.value })} data-testid="payments-tax-city" />
          </Field>
          <Field label="State / region">
            <input className="sc-input" value={tax.region} onChange={(e) => setTax({ ...tax, region: e.target.value })} data-testid="payments-tax-region" />
          </Field>
          <Field label="Postal code">
            <input className="sc-input" value={tax.postal} onChange={(e) => setTax({ ...tax, postal: e.target.value })} data-testid="payments-tax-postal" />
          </Field>
          <Field label="W-9 acknowledgement" fullWidth>
            <label className="flex items-start gap-2 cursor-pointer text-xs text-zinc-300">
              <input type="checkbox" checked={tax.w9_ack} onChange={(e) => setTax({ ...tax, w9_ack: e.target.checked })} className="mt-0.5" data-testid="payments-tax-w9" />
              <span>I certify the information above is accurate and I am subject to backup withholding rules as applicable.</span>
            </label>
          </Field>
        </FieldGrid>
      </SectionBlock>

      <SaveBar dirty={!!dirty} onSave={save} onReset={() => setTax(initial)} busy={busy} />
    </div>
  );
}
